// Preset Style Themes using Dromo's actual style properties
export type StylePreset = {
  name: string;
  description: string;
  previewColor: string;
  [key: string]: any;
};

export const stylePresets: Record<string, StylePreset> = {
  default: {
    name: "Default",
    description: "Royal blue with clean styling",
    previewColor: "#4169E1",
    global: {
      backgroundColor: "#EEF2FF",
      primaryTextColor: "#1F2937",
      secondaryTextColor: "#6B7280",
      textColor: "#374151",
      successColor: "#10B981",
      warningColor: "#F59E0B",
      borderColor: "#C7D2FE",
      borderRadius: "12px",
    },
    primaryButton: {
      backgroundColor: "#4169E1",
      textColor: "#FFFFFF",
      borderRadius: "12px",
      hoverBackgroundColor: "#2F4B99",
      hoverTextColor: "#FFFFFF",
    },
    dataTable: {
      headerBackgroundColor: "#F3F4F6",
      headerTextColor: "#1F2937",
      headerFontWeight: "600",
      cellBackgroundColor: "#FFFFFF",
      cellTextColor: "#374151",
      cellHoverBackgroundColor: "#F9FAFB",
      cellHoverTextColor: "#1F2937",
      dividerColor: "#E5E7EB",
      borderColor: "#E5E7EB",
      accentColor: "#4169E1",
    },
  },
  dark: {
    name: "Dark Mode",
    description: "Complete dark theme with blue accents",
    previewColor: "#3b82f6",
    global: {
      backgroundColor: "#1a1a1a",
      primaryTextColor: "#f9fafb",
      secondaryTextColor: "#9ca3af",
      textColor: "#e5e7eb",
      successColor: "#10b981",
      warningColor: "#f59e0b",
      borderColor: "#374151",
      borderRadius: "8px",
      backdropBlur: true,
    },
    card: {
      backgroundColor: "#1f2937",
      containerBackgroundColor: "#111827",
      borderColor: "#374151",
      borderWidth: "1px",
      borderRadius: "0.75rem",
      textColor: "#f9fafb",
      expandedBackgroundColor: "#111827",
      iconColor: "#9ca3af",
    },
    dataTable: {
      headerBackgroundColor: "#111827",
      headerTextColor: "#f9fafb",
      headerFontWeight: "500",
      headerActiveBackgroundColor: "#1e3a8a",
      headerActiveTextColor: "#ffffff",
      headerHighlightedBackgroundColor: "#1e40af",
      headerHighlightedTextColor: "#ffffff",
      rowHeaderBackgroundColor: "#111827",
      rowHeaderTextColor: "#f9fafb",
      cornerHeaderBackgroundColor: "#111827",
      cellBackgroundColor: "#1f2937",
      cellTextColor: "#e5e7eb",
      cellHoverBackgroundColor: "#374151",
      cellHoverTextColor: "#f9fafb",
      oddRowBackgroundColor: "#374151",
      cellSelectionBorderColor: "#3b82f6",
      cellSelectionBackgroundColor: "rgba(59, 130, 246, 0.1)",
      rowSelectedBackgroundColor: "rgba(59, 130, 246, 0.3)",
      rowSelectedBorderColor: "#3b82f6",
      borderColor: "#374151",
      dividerColor: "#4b5563",
      errorCellBackgroundColor: "#7f1d1d",
      errorCellHoverBackgroundColor: "#991b1b",
      warningCellBackgroundColor: "#713f12",
      warningCellHoverBackgroundColor: "#854d0e",
      infoCellBackgroundColor: "#1e3a8a",
      infoCellHoverBackgroundColor: "#1e40af",
      readOnlyCellBackgroundColor: "#374151",
      readOnlyCellTextColor: "#9ca3af",
      accentColor: "#3b82f6",
    },
    primaryButton: {
      backgroundColor: "#3b82f6",
      textColor: "#ffffff",
      borderRadius: "0.375rem",
      hoverBackgroundColor: "#2563eb",
      hoverTextColor: "#ffffff",
    },
    secondaryButton: {
      backgroundColor: "#374151",
      textColor: "#f9fafb",
      border: "1px solid #4b5563",
      borderRadius: "0.375rem",
      hoverBackgroundColor: "#4b5563",
      hoverTextColor: "#f9fafb",
    },
    tertiaryButton: {
      backgroundColor: "transparent",
      textColor: "#3b82f6",
      border: "1px solid transparent",
      borderRadius: "0.375rem",
      hoverBackgroundColor: "#1f2937",
      hoverTextColor: "#60a5fa",
    },
    dropdown: {
      backgroundColor: "#1f2937",
      borderColor: "#374151",
      textColor: "#f9fafb",
      menuBackgroundColor: "#1f2937",
      optionHoverBackgroundColor: "#374151",
      optionSelectedBackgroundColor: "#4b5563",
    },
    dropzone: {
      borderWidth: 2,
      borderRadius: 8,
      backgroundColor: "#1f2937",
      borderColor: "#4b5563",
      borderStyle: "dashed",
      color: "#f9fafb",
    },
    stepperBar: {
      backgroundColor: "#111827",
      borderBottom: "1px solid #374151",
      currentColor: "#3b82f6",
      completeColor: "#10b981",
      incompleteColor: "#6b7280",
      completeFontWeight: "600",
      currentFontWeight: "600",
    },
    modalOverlay: {
      backgroundColor: "#000000",
      opacity: "75",
    },
  },
  professional: {
    name: "Professional",
    description: "Clean business aesthetic",
    previewColor: "#2563EB",
    global: {
      backgroundColor: "#DBEAFE",
      primaryTextColor: "#0F172A",
      secondaryTextColor: "#64748B",
      textColor: "#1E3A8A",
      successColor: "#10B981",
      warningColor: "#F59E0B",
      borderColor: "#93C5FD",
      borderRadius: "6px",
    },
    primaryButton: {
      backgroundColor: "#2563EB",
      textColor: "#FFFFFF",
      borderRadius: "6px",
      hoverBackgroundColor: "#1E40AF",
      hoverTextColor: "#FFFFFF",
    },
    secondaryButton: {
      backgroundColor: "#F1F5F9",
      textColor: "#334155",
      border: "1px solid #CBD5E1",
      borderRadius: "6px",
      hoverBackgroundColor: "#E2E8F0",
      hoverTextColor: "#1E293B",
    },
    dataTable: {
      headerBackgroundColor: "#1E293B",
      headerTextColor: "#FFFFFF",
      headerFontWeight: "600",
      cellBackgroundColor: "#FFFFFF",
      cellTextColor: "#0F172A",
      cellHoverBackgroundColor: "#F1F5F9",
      cellHoverTextColor: "#0F172A",
      dividerColor: "#E2E8F0",
      borderColor: "#CBD5E1",
      accentColor: "#2563EB",
    },
  },
  vibrant: {
    name: "Vibrant",
    description: "Bold and colorful",
    previewColor: "#EC4899",
    global: {
      backgroundColor: "#FCE7F3",
      primaryTextColor: "#831843",
      secondaryTextColor: "#BE185D",
      textColor: "#9F1239",
      successColor: "#10B981",
      warningColor: "#F59E0B",
      borderColor: "#F9A8D4",
      borderRadius: "16px",
    },
    primaryButton: {
      backgroundColor: "#EC4899",
      textColor: "#FFFFFF",
      borderRadius: "16px",
      hoverBackgroundColor: "#DB2777",
      hoverTextColor: "#FFFFFF",
    },
    secondaryButton: {
      backgroundColor: "#FCE7F3",
      textColor: "#BE185D",
      border: "1px solid #F9A8D4",
      borderRadius: "16px",
      hoverBackgroundColor: "#FBCFE8",
      hoverTextColor: "#9F1239",
    },
    dataTable: {
      headerBackgroundColor: "#DB2777",
      headerTextColor: "#FFFFFF",
      headerFontWeight: "600",
      cellBackgroundColor: "#FFFFFF",
      cellTextColor: "#831843",
      cellHoverBackgroundColor: "#FCE7F3",
      cellHoverTextColor: "#831843",
      dividerColor: "#F9A8D4",
      borderColor: "#F9A8D4",
      accentColor: "#EC4899",
    },
  },
  minimal: {
    name: "Minimal",
    description: "Simple black and white",
    previewColor: "#000000",
    global: {
      backgroundColor: "#F3F4F6",
      primaryTextColor: "#000000",
      secondaryTextColor: "#6B7280",
      textColor: "#1F2937",
      successColor: "#10B981",
      warningColor: "#F59E0B",
      borderColor: "#D1D5DB",
      borderRadius: "4px",
    },
    primaryButton: {
      backgroundColor: "#000000",
      textColor: "#FFFFFF",
      borderRadius: "4px",
      hoverBackgroundColor: "#374151",
      hoverTextColor: "#FFFFFF",
    },
    secondaryButton: {
      backgroundColor: "#FFFFFF",
      textColor: "#000000",
      border: "1px solid #E5E7EB",
      borderRadius: "4px",
      hoverBackgroundColor: "#F9FAFB",
      hoverTextColor: "#000000",
    },
    dataTable: {
      headerBackgroundColor: "#F9FAFB",
      headerTextColor: "#000000",
      headerFontWeight: "600",
      cellBackgroundColor: "#FFFFFF",
      cellTextColor: "#000000",
      cellHoverBackgroundColor: "#F3F4F6",
      cellHoverTextColor: "#000000",
      dividerColor: "#E5E7EB",
      borderColor: "#E5E7EB",
      accentColor: "#000000",
    },
  },
};

export const stylePresetToOverrides = (themeKey: string): Record<string, any> | undefined => {
  if (themeKey === "default") return undefined;
  const { name, description, previewColor, ...styleOverrides } = stylePresets[themeKey];
  return styleOverrides;
};
